import { useQuery } from "@tanstack/react-query";
import { useFormContext } from "react-hook-form";

import { apiClient } from "~/api/ApiClient.ts";
import { useTeam } from "~/contexts/TeamProvider.tsx";
import { canMember } from "~/helpers/authorizations";

import FormLabel from "../login/formLabel/FormLabel";
import Switch from "../switch/Switch";
import RangeSlider from "../ui/rangeSlider/RangeSlider";
import styles from "./Settings.module.scss";

const TeamAIEngine = () => {
  const { authorizations: teamAuthorizations } = useTeam();
  const canUpdateTeam = canMember(teamAuthorizations, "update");

  const { register, setValue, watch } = useFormContext();

  const { data: planUsage } = useQuery({
    queryKey: ["planUsage"],
    queryFn: () => apiClient.teams.usageList(),
  });
  const pageMax = planUsage?.data?.team_usage?.team_limits?.page_max || 0;

  const metaSwitchEnabled = watch("ai_description");
  const schemaSwitchEnabled = watch("ai_schema");
  const aiTitle = watch("ai_title");
  const aiMetaOverride = watch("ai_meta_override");
  const aiSchemaOverride = watch("ai_schema_override");

  const aiMetaOverrideLimitTitle = () => {
    if (aiMetaOverride === -1) {
      return "unlimited";
    }

    return aiMetaOverride;
  };

  const aiSchemaOverrideLimitTitle = () => {
    if (aiSchemaOverride === -1) {
      return "unlimited";
    }

    return aiSchemaOverride;
  };

  const handleSliderChange = (field: string) => (newValue: number) => {
    const value = newValue === 0 ? -1 : newValue;
    setValue(field, value, { shouldDirty: true });
  };

  return (
    // @ts-expect-error TS2551 non-existing className
    <div className={styles.settingsSectionGroup}>
      <p className={styles.settingsText}>
        Allows you to configure AI engine settings for your team. Here, you can
        set the global limits that will affect all our projects within a team
      </p>
      <div className={styles.settingsTeamForm}>
        <div className={styles.settingsSwitchGroup}>
          <div>
            <FormLabel label="Generate or optimize too short page titles" />
            <p className={styles.settingsSectionText}>
              Improves page titles for better user attention and search engine
              rankings
            </p>
          </div>
          <Switch
            checked={aiTitle}
            {...register("ai_title")}
            disabled={!canUpdateTeam}
          />
        </div>

        <div>
          <div className={styles.settingsSwitchGroup}>
            <div>
              <FormLabel label="Generate missing page meta description and titles" />
              <p className={styles.settingsSectionText}>
                Set the limits for the Meta Description and AI Title. Current
                limit {aiMetaOverrideLimitTitle()}
              </p>
            </div>
            <Switch
              disabled={!canUpdateTeam}
              checked={metaSwitchEnabled}
              {...register("ai_description")}
            />
          </div>

          {(metaSwitchEnabled || aiTitle) && (
            // @ts-expect-error TS2551 non-existing className
            <div className={styles.settingsSectionLeftGroup}>
              <div className={styles.settingsSliderContainer}>
                <RangeSlider
                  disabled={!canUpdateTeam}
                  value={aiMetaOverride === -1 ? 0 : aiMetaOverride}
                  defaultValue={aiMetaOverride === -1 ? 0 : aiMetaOverride}
                  onChange={handleSliderChange("ai_meta_override")}
                  min={0}
                  max={pageMax}
                  step={100}
                  valueSuffix={
                    aiMetaOverride === 0 || aiMetaOverride === -1
                      ? " (unlimited)"
                      : ""
                  }
                  showValue={true}
                />
              </div>
            </div>
          )}
        </div>
        <div>
          <div className={styles.settingsSwitchGroup}>
            <div>
              <FormLabel label="Generate JSON Schemas for pages" />
              <p className={styles.settingsSectionText}>
                {`Set the limits for the JSON schemas usage ${aiSchemaOverrideLimitTitle()}`}
              </p>
            </div>
            <Switch
              checked={schemaSwitchEnabled}
              {...register("ai_schema")}
              disabled={!canUpdateTeam}
            />
          </div>
          {schemaSwitchEnabled && (
            // @ts-expect-error TS2551 non-existing className
            <div className={styles.settingsSectionLeftGroup}>
              <div className={styles.settingsSliderContainer}>
                <RangeSlider
                  disabled={!canUpdateTeam}
                  value={aiSchemaOverride === -1 ? 0 : aiSchemaOverride}
                  defaultValue={aiSchemaOverride === -1 ? 0 : aiSchemaOverride}
                  onChange={handleSliderChange("ai_schema_override")}
                  min={0}
                  max={pageMax}
                  step={100}
                  valueSuffix={
                    aiSchemaOverride === 0 || aiSchemaOverride === -1
                      ? " (unlimited)"
                      : ""
                  }
                  showValue={true}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TeamAIEngine;
